'use client'
import React, { useState, useRef } from 'react'
import { FormValues, useFormContext } from './formContext'
import { supportedImgTypes, uploadSvg } from '../reportingTool/reportingUtils'

interface IProps {
  target: keyof FormValues
  index?: number
  hasError?: boolean
  id?: string
}

export const DropZone: React.FC<IProps> = ({ target, index, hasError, id }) => {
  const [isDragging, setIsDragging] = useState(false)
  const { handleDroppedFiles, data, setData } = useFormContext()

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    setIsDragging(false)
    const droppedFiles = event.dataTransfer.files
    if (droppedFiles.length > 0) {
      const validFile = supportedImgTypes.includes(event.dataTransfer.files?.[0].type)
      if (!validFile) {
        alert('Please enter a valid file type')
        return
      }
      const newFiles = Array.from(droppedFiles)
      if (target === 'eventMedia' && index !== undefined) {
        const updatedEventMedia = [...data.eventMedia]
        updatedEventMedia[index] = {
          ...updatedEventMedia[index],
          eventFootage: [...data.eventMedia[index].eventFootage, newFiles[0]],
        }
        setData({
          ...data,
          eventMedia: [...updatedEventMedia],
        })
      } else {
        handleDroppedFiles(target, newFiles)
      }
    }
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const validFile = supportedImgTypes.includes(e.target.files[0].type)
      if (validFile) {
        const newFiles = Array.from(e.target.files)
        if (target === 'eventMedia' && index !== undefined) {
          const updatedEventMedia = [...data.eventMedia]
          updatedEventMedia[index] = {
            ...updatedEventMedia[index],
            eventFootage: [...data.eventMedia[index].eventFootage, newFiles[0]],
          }
          setData({
            ...data,
            eventMedia: [...updatedEventMedia],
          })
        } else {
          handleDroppedFiles(target, newFiles)
        }
      } else {
        alert('Please enter a valid file type')
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault() // Prevent default scrolling behavior for Space key
      fileInputRef.current?.click()
    }
  }

  return (
    <div
      className={`flex flex-col items-center justify-center mb:w-[600px] min-h-[215px] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-blue-300 border-[2px] border-dashed ${
        isDragging ? 'bg-blue-100 border-gray-500' : 'bg-white border-blue-300'
      } ${hasError ? 'border-red-500' : ''}`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={(e) => handleClick(e)}
      id={id}
      role={'button'}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className={'m-2'}>{uploadSvg}</div>
      <div>
        <strong>Click to upload</strong> or drag and drop
      </div>
      <div className="text-[14px] font-semibold leading-[21px] mb-1">Max. File Size: 100MB</div>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={supportedImgTypes.join(',')}
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  )
}
